import { FallbackError } from "@core/domain/errors/fallback.error";
import type { Nullable } from "@core/domain/types/nullable.type";
import { FarmDatasource } from "@farm/data/datasources/farm.datasource";
import type { FarmCreateError } from "@farm/domain/errors/farm-create.error";
import type { FarmUpdateError } from "@farm/domain/errors/farm-update.error";
import type { LayerCreateError } from "@farm/domain/errors/layer-create.error";
import type { PlotUpdateError } from "@farm/domain/errors/plot-update.error";
import type { PlotUploadFileError } from "@farm/domain/errors/plot-upload-file.error";
import type {
    Farm,
    FarmCreate,
    Farms,
    FarmUpdate,
} from "@farm/domain/models/farm.model";
import type { LayerType } from "@farm/domain/models/layer-type.model";
import type { Layer, LayerCreate } from "@farm/domain/models/layer.model";
import type {
    Plot,
    PlotFile,
    PlotFileUpload,
    PlotUpdate,
} from "@farm/domain/models/plot.model";
import type { FarmRepository } from "@farm/domain/repositories/farm.repository";
import { inject, injectable } from "inversify";
import type { ResultAsync } from "neverthrow";

@injectable()
export class FarmImplRepository implements FarmRepository {
    constructor(
        @inject(FarmDatasource)
        private readonly farmDatasource: FarmDatasource,
    ) {}

    getAll(): ResultAsync<Farms, FallbackError> {
        return this.farmDatasource.fetchAll();
    }

    getById(farmId: number): ResultAsync<Farm, FallbackError> {
        return this.farmDatasource.fetchById(farmId);
    }

    create(createFarm: FarmCreate): ResultAsync<Farm, FarmCreateError> {
        return this.farmDatasource.create(createFarm);
    }

    update(
        farmId: number,
        updateFarm: FarmUpdate,
    ): ResultAsync<Farm, FarmUpdateError> {
        return this.farmDatasource.update(farmId, updateFarm);
    }

    delete(farmId: number): ResultAsync<true, FallbackError> {
        return this.farmDatasource.delete(farmId);
    }

    addPlot(
        farmId: number,
        geojson: GeoJSON.Polygon,
    ): ResultAsync<Plot, FallbackError> {
        return this.farmDatasource.addPlot(farmId, geojson);
    }

    updatePlot(
        farmId: number,
        updatePlot: PlotUpdate,
    ): ResultAsync<Plot, PlotUpdateError> {
        return this.farmDatasource.updatePlot(farmId, updatePlot);
    }

    deletePlot(
        farmId: number,
        plotId: string,
    ): ResultAsync<true, FallbackError> {
        return this.farmDatasource.deletePlot(farmId, plotId);
    }

    uploadFile(
        plotId: string,
        farmId: string,
        plotFile: PlotFileUpload,
    ): ResultAsync<PlotFile, PlotUploadFileError> {
        return this.farmDatasource.uploadFile(plotId, farmId, plotFile);
    }

    deletePlotFile(
        farmId: string,
        plotId: string,
    ): ResultAsync<true, FallbackError> {
        return this.farmDatasource.deletePlotFile(farmId, plotId);
    }

    async getLayerTypes(sources: string[]): Promise<LayerType[]> {
        return this.farmDatasource.fetchAllLayerTypes(sources);
    }

    fetchLayer(
        farmId: string,
        plotId: string,
        createLayer: LayerCreate,
    ): ResultAsync<Nullable<Layer>, LayerCreateError> {
        return this.farmDatasource.fetchLayer(farmId, plotId, createLayer);
    }
}
